import { useCallback, useEffect, useState } from 'react';

import { api, mutate, uploadFile, openFileInline } from '../shared/api';
import { Page, PageHeader, Card, Btn, Select, Pill, DataTable, Tabs, ui, type Col } from '../shared/ui';

type Locale = 'hr' | 'en';

type Competency = { key: string; label: string; desc: string };
type State = {
    can_manage: boolean;
    read_only: boolean;
    competency_framework: Competency[];
    rating_anchors: Record<string, string>;
    recommendations: string[];
    talentlyft_enabled: boolean;
};
type Position = {
    id: number;
    title: string;
    location: string | null;
    disqualifiers: string | null;
    key_questions: string[];
    status: string;
    external_id: string | null;
    candidate_count: number;
};
type Candidate = {
    id: number;
    position_id: number;
    position_title: string | null;
    name: string;
    email: string | null;
    phone: string | null;
    expected_salary_eur: number | null;
    gross_salary_eur: number | null;
    notice_period: string | null;
    comment: string | null;
    stage: string;
    evaluation_count: number;
    recommendations: string[];
    disqualified: boolean;
    avg_rating: number | null;
    cv_original_name?: string | null;
    cv_uploaded_at?: string | null;
    has_photo?: boolean | number;
};
// Bilješka po kompetenciji (PRIMJER_OCJENE): konkretan dokaz, crvena zastavica (red flag), follow-up.
type EvidenceNote = { note?: string; red_flag?: boolean; follow_up?: string };
type Evaluation = {
    id: number;
    evaluator_id: number | null;
    evaluator_name: string | null;
    round: string;
    score_knowledge: number | null;
    score_skills: number | null;
    score_attitude: number | null;
    score_experience: number | null;
    score_personality: number | null;
    score_values: number | null;
    score_capacity: number | null;
    // Bilješka po kompetenciji: dokaz + crvena zastavica + follow-up (PRIMJER_OCJENE iz radne tablice).
    // Kompatibilno unatrag: stara vrijednost je bio goli string (samo dokaz).
    evidence: Record<string, EvidenceNote | string>;
    disqualifier_triggered: boolean | number;
    disqualifier_note: string | null;
    overall_rating: number | null;
    recommendation: string | null;
    second_round_focus: string | null;
};
type CandidateDetail = { candidate: Candidate; position: Position | null; evaluations: Evaluation[] };

const recTone: Record<string, 'ok' | 'warn' | 'bad'> = { DA: 'ok', MOZDA: 'warn', NE: 'bad' };
const field: React.CSSProperties = { height: 38, padding: '0 12px', borderRadius: ui.radiusSm, border: `1px solid ${ui.line}`, fontSize: '0.86rem', color: ui.ink, background: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
const label: React.CSSProperties = { display: 'block', fontSize: '0.78rem', fontWeight: 650, color: ui.ink2, marginBottom: 5 };

const T = {
    hr: {
        title: 'Selekcija kandidata', subtitle: '1. krug — iste ključne pitanja svim kandidatima za istu poziciju. Bilježite dokaz, ne dojam.',
        positions: 'Pozicije', candidates: 'Kandidati', newPosition: 'Nova pozicija', newCandidate: 'Novi kandidat',
        title_: 'Naziv pozicije', location: 'Lokacija', disq: 'Isključivači (jedan po retku)', keyQ: 'Ključna pitanja (jedno po retku)',
        name: 'Ime i prezime', email: 'E-mail', phone: 'Telefon', expected: 'Očekivana plaća (EUR)', gross: 'Bruto plaća (EUR)',
        notice: 'Otkazni rok', comment: 'Komentar', save: 'Spremi', cancel: 'Odustani',
        evalTitle: 'Ocjena 1. kruga', evaluator: 'Voditelj', overall: 'Ukupna ocjena 1-5', recommendation: 'Preporuka',
        secondRound: 'Što provjeriti u 2. krugu', evidence: 'Dokaz (konkretan primjer)', disqTrig: 'Aktiviran isključivač',
        disqNote: 'Napomena isključivača', readonly: 'Pristup samo za čitanje (auditor).', noEvals: 'Nema ocjena voditelja još.',
        importTL: 'Uvezi iz TalentLyft', back: 'Natrag', stage: 'Faza', avg: 'Prosjek',
        redFlag: 'Crvena zastavica', followUp: 'Follow-up (dodatno provjeriti)',
        uploadCv: 'Podigni životopis (CV)', uploadCvLead: 'PDF, DOCX ili TXT. Kandidat i prijedlog ocjene (AI čitanje CV-a) kreiraju se automatski.',
        cvPosition: 'Pozicija za CV', cvFile: 'Datoteka CV-a', cvUpload: 'Podigni i kreiraj kandidata', cvDownload: 'Preuzmi CV',
        cvParsed: 'Iz CV-a prepoznato', cvWarn: 'Napomena', cvNone: 'CV nije priložen.',
        aiTitle: 'AI prijedlog iz CV-a', aiLead: 'Dokaz koji AI vidi u CV-u po kompetenciji. Prijedlog, ne konačna ocjena — voditelj potvrđuje nakon razgovora.',
        aiWatch: 'Moguć isključivač (provjeriti)', noAi: 'CV nije pročitan automatski — unesi podatke ručno.',
        quickFacts: 'Osnovni podaci', disqualifiers_: 'Isključivači pozicije', keyQuestions: 'Ključna pitanja',
        delete: 'Obriši kandidata', deleteConfirm: 'Sigurno obrisati kandidata?',
        edit: 'Uredi', editSave: 'Spremi izmjene',
    },
    en: {
        title: 'Candidate selection', subtitle: 'Round 1 — same key questions for all candidates on a position. Record evidence, not impressions.',
        positions: 'Positions', candidates: 'Candidates', newPosition: 'New position', newCandidate: 'New candidate',
        title_: 'Position title', location: 'Location', disq: 'Disqualifiers (one per line)', keyQ: 'Key questions (one per line)',
        name: 'Full name', email: 'Email', phone: 'Phone', expected: 'Expected salary (EUR)', gross: 'Gross salary (EUR)',
        notice: 'Notice period', comment: 'Comment', save: 'Save', cancel: 'Cancel',
        evalTitle: 'Round 1 evaluation', evaluator: 'Interviewer', overall: 'Overall rating 1-5', recommendation: 'Recommendation',
        secondRound: 'Second-round focus', evidence: 'Evidence (concrete example)', disqTrig: 'Disqualifier triggered',
        disqNote: 'Disqualifier note', readonly: 'Read-only access (auditor).', noEvals: 'No interviewer evaluations yet.',
        importTL: 'Import from TalentLyft', back: 'Back', stage: 'Stage', avg: 'Average',
        redFlag: 'Red flag', followUp: 'Follow-up (check further)',
        uploadCv: 'Upload resume (CV)', uploadCvLead: 'PDF, DOCX or TXT. Candidate and a draft evaluation (AI reading of the CV) are created automatically.',
        cvPosition: 'Position for CV', cvFile: 'CV file', cvUpload: 'Upload and create candidate', cvDownload: 'Download CV',
        cvParsed: 'Recognized from CV', cvWarn: 'Note', cvNone: 'No CV attached.',
        aiTitle: 'AI draft from CV', aiLead: 'Evidence the AI sees in the CV per competency. A draft, not a final rating — the interviewer confirms after the conversation.',
        aiWatch: 'Possible disqualifier (check)', noAi: 'CV was not read automatically — enter data manually.',
        quickFacts: 'Key facts', disqualifiers_: 'Position disqualifiers', keyQuestions: 'Key questions',
        delete: 'Delete candidate', deleteConfirm: 'Are you sure you want to delete this candidate?',
        edit: 'Edit', editSave: 'Save changes',
    },
};

/** Inicijali iz imena za placeholder avatar. */
function initials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (name.slice(0, 2)).toUpperCase();
}

const avatarSize = 72;
const avatarStyle: React.CSSProperties = { width: avatarSize, height: avatarSize, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${ui.line}`, flexShrink: 0 };
const avatarPlaceholder: React.CSSProperties = { ...avatarStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', background: ui.bg, color: ui.ink2, fontSize: '1.3rem', fontWeight: 800, letterSpacing: '0.05em' };

const STAGES = ['prijava', '1_krug', '2_krug', 'odbijen', 'odustao'];

export function HrCandidateSelectionModule({ token, locale }: { token: string; locale: Locale }) {
    const t = T[locale];
    const [state, setState] = useState<State | null>(null);
    const [positions, setPositions] = useState<Position[]>([]);
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [tab, setTab] = useState<'positions' | 'candidates'>('candidates');
    const [detail, setDetail] = useState<CandidateDetail | null>(null);
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);

    const loadLists = useCallback(async () => {
        try {
            const [p, c] = await Promise.all([
                api<{ positions: Position[] }>('/hr-candidates/positions', token),
                api<{ candidates: Candidate[] }>('/hr-candidates/candidates', token),
            ]);
            setPositions(p.positions);
            setCandidates(c.candidates);
        } catch (e) { setErr((e as Error).message); }
    }, [token]);

    useEffect(() => {
        (async () => {
            try {
                const s = await api<State>('/hr-candidates/state', token);
                setState(s);
                await loadLists();
            } catch (e) { setErr((e as Error).message); }
        })();
    }, [token, loadLists]);

    const openCandidate = useCallback(async (id: number) => {
        try {
            const d = await api<CandidateDetail>(`/hr-candidates/candidates/${id}`, token);
            setDetail(d);
        } catch (e) { setErr((e as Error).message); }
    }, [token]);

    if (!state) {
        return <div style={{ padding: 24 }}>{err ? <p style={{ color: '#dc2626' }}>{err}</p> : 'Učitavanje…'}</div>;
    }

    return (
        <Page>
            <PageHeader icon="🧭" title={t.title} subtitle={t.subtitle}
                actions={!detail ? <Tabs tabs={[{ key: 'candidates', label: t.candidates }, { key: 'positions', label: t.positions }]} active={tab} onChange={setTab} /> : undefined} />
            {state.read_only ? <Pill tone="warn">{t.readonly}</Pill> : null}
            {err ? <div style={{ color: '#dc2626', fontSize: '0.86rem' }}>{err}</div> : null}

            {detail ? (
                <CandidateWorkspace
                    detail={detail} state={state} token={token} locale={locale}
                    onBack={() => { setDetail(null); loadLists(); }}
                    onReload={() => openCandidate(detail.candidate.id)}
                />
            ) : tab === 'positions' ? (
                <PositionsTab
                    positions={positions} state={state} token={token} locale={locale} busy={busy} setBusy={setBusy}
                    onChange={loadLists} onErr={setErr}
                />
            ) : (
                <CandidatesTab
                    candidates={candidates} positions={positions} state={state} token={token} locale={locale}
                    busy={busy} setBusy={setBusy} onOpen={openCandidate} onChange={loadLists} onErr={setErr}
                />
            )}
        </Page>
    );
}

// ── POZICIJE ────────────────────────────────────────────────────────────────
function PositionsTab({ positions, state, token, locale, busy, setBusy, onChange, onErr }: {
    positions: Position[]; state: State; token: string; locale: Locale; busy: boolean;
    setBusy: (b: boolean) => void; onChange: () => void; onErr: (s: string) => void;
}) {
    const t = T[locale];
    const [form, setForm] = useState(false);
    const [title, setTitle] = useState('');
    const [location, setLocation] = useState('');
    const [disq, setDisq] = useState('');
    const [keyQ, setKeyQ] = useState('');

    const save = async () => {
        setBusy(true); onErr('');
        try {
            await mutate('/hr-candidates/positions', token, 'POST', {
                title, location: location || null, disqualifiers: disq || null,
                key_questions: keyQ.split('\n').map((s) => s.trim()).filter(Boolean),
            });
            setForm(false); setTitle(''); setLocation(''); setDisq(''); setKeyQ(''); onChange();
        } catch (e) { onErr((e as Error).message); } finally { setBusy(false); }
    };

    const doImport = async (id: number) => {
        setBusy(true); onErr('');
        try {
            const r = await mutate<{ ok: boolean; imported?: number; message?: string }>(`/hr-candidates/positions/${id}/talentlyft-import`, token, 'POST');
            if (!r.ok) { onErr(r.message ?? 'TalentLyft import nije uspio.'); } else { onChange(); }
        } catch (e) { onErr((e as Error).message); } finally { setBusy(false); }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {state.can_manage ? (
                form ? (
                    <Card pad={18}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <label><span style={label}>{t.title_}</span><input style={field} value={title} onChange={(e) => setTitle(e.target.value)} /></label>
                            <label><span style={label}>{t.location}</span><input style={field} value={location} onChange={(e) => setLocation(e.target.value)} /></label>
                        </div>
                        <label style={{ display: 'block', marginTop: 12 }}><span style={label}>{t.disq}</span><textarea style={{ ...field, height: 'auto', padding: 10, minHeight: 60 }} value={disq} onChange={(e) => setDisq(e.target.value)} /></label>
                        <label style={{ display: 'block', marginTop: 12 }}><span style={label}>{t.keyQ}</span><textarea style={{ ...field, height: 'auto', padding: 10, minHeight: 80 }} value={keyQ} onChange={(e) => setKeyQ(e.target.value)} /></label>
                        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                            <Btn variant="primary" disabled={busy || !title} onClick={save}>{t.save}</Btn>
                            <Btn variant="ghost" onClick={() => setForm(false)}>{t.cancel}</Btn>
                        </div>
                    </Card>
                ) : <div><Btn variant="primary" onClick={() => setForm(true)}>+ {t.newPosition}</Btn></div>
            ) : null}

            {positions.map((p) => (
                <Card key={p.id} pad={18}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                        <div>
                            <div style={{ fontWeight: 700, color: ui.ink, fontSize: '1rem' }}>{p.title}</div>
                            {p.location ? <div style={{ color: ui.muted, fontSize: '0.82rem', marginTop: 2 }}>{p.location}</div> : null}
                        </div>
                        <Pill tone="info">{p.candidate_count} kand.</Pill>
                    </div>
                    {p.disqualifiers ? <div style={{ marginTop: 10, fontSize: '0.84rem', color: ui.ink2 }}><strong style={{ color: ui.ink }}>Isključivači:</strong> {p.disqualifiers}</div> : null}
                    {p.key_questions.length ? (
                        <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>{p.key_questions.map((q, i) => <li key={i} style={{ fontSize: '0.84rem', color: ui.ink2 }}>{q}</li>)}</ul>
                    ) : null}
                    {state.can_manage && state.talentlyft_enabled && p.external_id ? (
                        <div style={{ marginTop: 10 }}><Btn variant="ghost" size="sm" disabled={busy} onClick={() => doImport(p.id)}>{t.importTL}</Btn></div>
                    ) : null}
                </Card>
            ))}
        </div>
    );
}

// ── KANDIDATI ────────────────────────────────────────────────────────────────
function CandidatesTab({ candidates, positions, state, token, locale, busy, setBusy, onOpen, onChange, onErr }: {
    candidates: Candidate[]; positions: Position[]; state: State; token: string; locale: Locale; busy: boolean;
    setBusy: (b: boolean) => void; onOpen: (id: number) => void; onChange: () => void; onErr: (s: string) => void;
}) {
    const t = T[locale];
    const [form, setForm] = useState(false);
    const [cvForm, setCvForm] = useState(false);
    const [cvPositionId, setCvPositionId] = useState<number | ''>('');
    const [cvFile, setCvFile] = useState<File | null>(null);
    const [cvResult, setCvResult] = useState<{ name: string; skills: string[]; years_experience: number | null; warnings: string[]; ai_summary: string | null; ai_possible_disqualifier: string | null } | null>(null);
    const [positionId, setPositionId] = useState<number | ''>('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [expected, setExpected] = useState('');
    const [gross, setGross] = useState('');
    const [notice, setNotice] = useState('');
    const [comment, setComment] = useState('');

    const save = async () => {
        if (positionId === '') return;
        setBusy(true); onErr('');
        try {
            await mutate('/hr-candidates/candidates', token, 'POST', {
                position_id: positionId, name, email: email || null, phone: phone || null,
                expected_salary_eur: expected ? Number(expected) : null,
                gross_salary_eur: gross ? Number(gross) : null,
                notice_period: notice || null, comment: comment || null,
            });
            setForm(false); setName(''); setEmail(''); setPhone(''); setExpected(''); setGross(''); setNotice(''); setComment('');
            onChange();
        } catch (e) { onErr((e as Error).message); } finally { setBusy(false); }
    };

    const uploadCv = async () => {
        if (cvPositionId === '' || !cvFile) return;
        setBusy(true); onErr(''); setCvResult(null);
        try {
            const body = new FormData();
            body.append('position_id', String(cvPositionId));
            body.append('cv', cvFile);
            const r = await uploadFile<{ id: number; name: string; email: string | null; phone: string | null; skills: string[]; years_experience: number | null; warnings: string[]; ai_summary: string | null; ai_possible_disqualifier: string | null }>(
                '/hr-candidates/candidates/cv-upload', token, body,
            );
            setCvResult({ name: r.name, skills: r.skills, years_experience: r.years_experience, warnings: r.warnings, ai_summary: r.ai_summary, ai_possible_disqualifier: r.ai_possible_disqualifier });
            setCvFile(null);
            onChange();
        } catch (e) { onErr((e as Error).message); } finally { setBusy(false); }
    };

    const cols: Col<Candidate>[] = [
        { key: 'name', label: t.name, render: (c) => (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                {c.has_photo ? (
                    <img src={`/api/hr-candidates/candidates/${c.id}/photo`} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${ui.line}` }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                ) : (
                    <span style={{ width: 32, height: 32, borderRadius: '50%', background: ui.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 800, color: ui.ink2, flexShrink: 0 }}>{initials(c.name)}</span>
                )}
                <span style={{ fontWeight: 600 }}>{c.name}{c.disqualified ? <span style={{ color: '#dc2626', marginLeft: 6, fontWeight: 700 }} title="Isključivač aktiviran">X</span> : null}</span>
            </span>
        ) },
        { key: 'position_title', label: t.positions, render: (c) => c.position_title ?? '—' },
        { key: 'stage', label: t.stage, render: (c) => <Pill tone={c.stage === 'odbijen' || c.stage === 'odustao' ? 'bad' : c.stage === '2_krug' ? 'ok' : 'neutral'}>{c.stage.replace('_', '. ')}</Pill> },
        { key: 'avg_rating', label: t.avg, align: 'right', render: (c) => c.avg_rating !== null ? <span style={{ fontWeight: 700, color: c.avg_rating >= 4 ? '#1f7a36' : c.avg_rating <= 2 ? '#dc2626' : ui.ink }}>{c.avg_rating.toFixed(1)}</span> : <span style={{ color: ui.muted }}>—</span> },
        {
            key: 'recommendations', label: t.recommendation, render: (c) => (
                <span style={{ display: 'inline-flex', gap: 4 }}>
                    {c.recommendations.length ? c.recommendations.map((r, i) => <Pill key={i} tone={recTone[r] ?? 'neutral'}>{r}</Pill>) : <span style={{ color: ui.muted }}>—</span>}
                </span>
            ),
        },
        ...(!state.read_only && state.can_manage ? [{
            key: '_delete' as keyof Candidate, label: '', align: 'right' as const, render: (c: Candidate) => (
                <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-block' }}>
                    <Btn variant="ghost" size="sm" disabled={busy} onClick={() => {
                        if (!window.confirm(t.deleteConfirm)) return;
                        setBusy(true); onErr('');
                        mutate(`/hr-candidates/candidates/${c.id}`, token, 'DELETE')
                            .then(() => onChange())
                            .catch((err: Error) => onErr(err.message))
                            .finally(() => setBusy(false));
                    }}><span style={{ color: '#dc2626', fontSize: '0.78rem' }}>X</span></Btn>
                </span>
            ),
        }] : []),
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {!state.read_only ? (
                cvForm ? (
                    <Card pad={18} style={{ borderColor: 'rgba(22,163,74,0.35)' }}>
                        <div style={{ fontWeight: 700, color: ui.ink, marginBottom: 2 }}>🤖 {t.uploadCv}</div>
                        <p style={{ margin: '0 0 12px', fontSize: '0.84rem', color: ui.muted }}>{t.uploadCvLead}</p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <label><span style={label}>{t.cvPosition}</span>
                                <Select value={String(cvPositionId)} onChange={(v) => setCvPositionId(v ? Number(v) : '')} width="100%">
                                    <option value="">—</option>
                                    {positions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                                </Select>
                            </label>
                            <label><span style={label}>{t.cvFile}</span>
                                <input type="file" accept=".pdf,.docx,.txt" style={{ ...field, padding: 6 }} onChange={(e) => setCvFile(e.target.files?.[0] ?? null)} />
                            </label>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                            <Btn variant="primary" disabled={busy || cvPositionId === '' || !cvFile} onClick={uploadCv}>{t.cvUpload}</Btn>
                            <Btn variant="ghost" onClick={() => { setCvForm(false); setCvResult(null); }}>{t.cancel}</Btn>
                        </div>
                        {cvResult ? (
                            <div style={{ marginTop: 14, padding: 12, background: ui.bg, borderRadius: ui.radiusSm, fontSize: '0.85rem' }}>
                                <div><strong>{t.cvParsed}:</strong> {cvResult.name}</div>
                                {cvResult.ai_summary ? <div style={{ marginTop: 6, color: ui.ink2 }}>{cvResult.ai_summary}</div> : null}
                                {cvResult.skills.length ? <div style={{ marginTop: 6 }}>{cvResult.skills.join(', ')}</div> : null}
                                {cvResult.years_experience !== null ? <div style={{ marginTop: 4 }}>~{cvResult.years_experience} god. iskustva</div> : null}
                                {cvResult.ai_possible_disqualifier ? <div style={{ marginTop: 6, color: '#9a6700', fontWeight: 650 }}>⚑ {t.aiWatch}: {cvResult.ai_possible_disqualifier}</div> : null}
                                {cvResult.warnings.map((w, i) => <div key={i} style={{ color: '#9a6700', marginTop: 4 }}>{t.cvWarn}: {w}</div>)}
                            </div>
                        ) : null}
                    </Card>
                ) : <div><Btn variant="primary" onClick={() => setCvForm(true)}>🤖 {t.uploadCv}</Btn></div>
            ) : null}

            {!state.read_only ? (
                form ? (
                    <Card pad={18}>
                        <label><span style={label}>{t.positions}</span>
                            <Select value={String(positionId)} onChange={(v) => setPositionId(v ? Number(v) : '')} width="100%">
                                <option value="">—</option>
                                {positions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                            </Select>
                        </label>
                        <label style={{ display: 'block', marginTop: 12 }}><span style={label}>{t.name}</span><input style={field} value={name} onChange={(e) => setName(e.target.value)} /></label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                            <label><span style={label}>{t.email}</span><input style={field} value={email} onChange={(e) => setEmail(e.target.value)} /></label>
                            <label><span style={label}>{t.phone}</span><input style={field} value={phone} onChange={(e) => setPhone(e.target.value)} /></label>
                            <label><span style={label}>{t.expected}</span><input style={field} type="number" value={expected} onChange={(e) => setExpected(e.target.value)} /></label>
                            <label><span style={label}>{t.gross}</span><input style={field} type="number" value={gross} onChange={(e) => setGross(e.target.value)} /></label>
                            <label><span style={label}>{t.notice}</span><input style={field} value={notice} onChange={(e) => setNotice(e.target.value)} /></label>
                        </div>
                        <label style={{ display: 'block', marginTop: 12 }}><span style={label}>{t.comment}</span><textarea style={{ ...field, height: 'auto', padding: 10, minHeight: 60 }} value={comment} onChange={(e) => setComment(e.target.value)} /></label>
                        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                            <Btn variant="primary" disabled={busy || !name || positionId === ''} onClick={save}>{t.save}</Btn>
                            <Btn variant="ghost" onClick={() => setForm(false)}>{t.cancel}</Btn>
                        </div>
                    </Card>
                ) : <div><Btn variant="ghost" onClick={() => setForm(true)}>+ {t.newCandidate}</Btn></div>
            ) : null}

            <Card>
                <DataTable cols={cols} rows={candidates} onRowClick={(c) => onOpen(c.id)} empty="Nema kandidata." />
            </Card>
        </div>
    );
}

// ── AI/CV PRIJEDLOG (posebno istaknut, evaluator_id === null) ─────────────────
function AiProposalCard({ ev, comp, locale }: { ev: Evaluation; comp: Competency[]; locale: Locale }) {
    const t = T[locale];
    return (
        <Card pad={18} style={{ borderColor: 'rgba(var(--ad-acc-rgb),0.35)', background: 'linear-gradient(180deg, rgba(var(--ad-acc-rgb),0.05), #fff 60px)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.1rem' }}>🤖</span>
                <div style={{ fontWeight: 700, color: ui.ink }}>{ev.evaluator_name ?? t.aiTitle}</div>
            </div>
            <p style={{ margin: '4px 0 12px', fontSize: '0.8rem', color: ui.muted }}>{t.aiLead}</p>
            {ev.disqualifier_note ? (
                <div style={{ padding: '8px 12px', background: '#fef3da', color: '#9a6700', borderRadius: ui.radiusSm, fontSize: '0.84rem', fontWeight: 600, marginBottom: 12 }}>
                    ⚑ {t.aiWatch}: {ev.disqualifier_note}
                </div>
            ) : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {comp.map((k) => {
                    const n = ev.evidence?.[k.key];
                    const note = typeof n === 'string' ? n : n?.note;
                    const followUp = typeof n === 'object' ? n?.follow_up : undefined;
                    const redFlag = typeof n === 'object' ? n?.red_flag : false;
                    if (!note) return null;
                    return (
                        <div key={k.key} style={{ borderTop: `1px solid ${ui.line2}`, paddingTop: 10 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: redFlag ? '#dc2626' : ui.ink2, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                {redFlag ? '⚑ ' : ''}{k.label}
                            </div>
                            <div style={{ fontSize: '0.86rem', color: ui.ink, marginTop: 2 }}>{note}</div>
                            {followUp ? <div style={{ fontSize: '0.8rem', color: ui.muted, marginTop: 2 }}><em>{t.followUp}:</em> {followUp}</div> : null}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}

// ── WORKSPACE: ocjena kandidata ───────────────────────────────────────────────
function CandidateWorkspace({ detail, state, token, locale, onBack, onReload }: {
    detail: CandidateDetail; state: State; token: string; locale: Locale; onBack: () => void; onReload: () => void;
}) {
    const t = T[locale];
    const c = detail.candidate;
    const comp = state.competency_framework;
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [editing, setEditing] = useState(false);
    const [editName, setEditName] = useState(c.name);
    const [editEmail, setEditEmail] = useState(c.email ?? '');
    const [editPhone, setEditPhone] = useState(c.phone ?? '');
    const [editExpected, setEditExpected] = useState(c.expected_salary_eur != null ? String(c.expected_salary_eur) : '');
    const [editGross, setEditGross] = useState(c.gross_salary_eur != null ? String(c.gross_salary_eur) : '');
    const [editNotice, setEditNotice] = useState(c.notice_period ?? '');
    const [editComment, setEditComment] = useState(c.comment ?? '');
    const [photoErr, setPhotoErr] = useState(false);

    const [scores, setScores] = useState<Record<string, number | ''>>({});
    const [evidence, setEvidence] = useState<Record<string, EvidenceNote>>({});
    const setNote = (key: string, patch: Partial<EvidenceNote>) =>
        setEvidence((s) => ({ ...s, [key]: { ...(s[key] ?? {}), ...patch } }));
    const [disq, setDisq] = useState(false);
    const [disqNote, setDisqNote] = useState('');
    const [overall, setOverall] = useState<number | ''>('');
    const [rec, setRec] = useState('');
    const [second, setSecond] = useState('');
    const [stage, setStage] = useState(c.stage);

    const aiEval = detail.evaluations.find((e) => e.evaluator_id === null);
    const humanEvals = detail.evaluations.filter((e) => e.evaluator_id !== null);

    const submit = async () => {
        setBusy(true); setErr('');
        try {
            const body: Record<string, unknown> = {
                round: '1', evidence,
                disqualifier_triggered: disq, disqualifier_note: disqNote || null,
                overall_rating: overall === '' ? null : overall,
                recommendation: rec || null, second_round_focus: second || null,
            };
            comp.forEach((k) => { body['score_' + k.key] = scores[k.key] === '' ? null : scores[k.key]; });
            await mutate(`/hr-candidates/candidates/${c.id}/evaluation`, token, 'POST', body);
            onReload();
        } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
    };

    const changeStage = async (s: string) => {
        setStage(s); setBusy(true); setErr('');
        try { await mutate(`/hr-candidates/candidates/${c.id}/stage`, token, 'PATCH', { stage: s }); onReload(); }
        catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
    };

    const saveEdit = async () => {
        setBusy(true); setErr('');
        try {
            await mutate(`/hr-candidates/candidates/${c.id}`, token, 'PATCH', {
                name: editName, email: editEmail || null, phone: editPhone || null,
                expected_salary_eur: editExpected ? Number(editExpected) : null,
                gross_salary_eur: editGross ? Number(editGross) : null,
                notice_period: editNotice || null, comment: editComment || null,
            });
            setEditing(false); onReload();
        } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
    };

    const doDelete = async () => {
        if (!window.confirm(t.deleteConfirm)) return;
        setBusy(true); setErr('');
        try {
            await mutate(`/hr-candidates/candidates/${c.id}`, token, 'DELETE');
            onBack();
        } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Btn variant="ghost" onClick={onBack}>{'\u2190'} {t.back}</Btn>
                {!state.read_only && state.can_manage ? (
                    <Btn variant="ghost" onClick={doDelete} disabled={busy}><span style={{ color: '#dc2626' }}>{t.delete}</span></Btn>
                ) : null}
            </div>

            {/* ── PROFIL KARTICA ── */}
            <Card pad={0}>
                <div style={{ background: `linear-gradient(135deg, ${ui.ink} 0%, #3a5a7c 100%)`, padding: '24px 24px 20px', borderRadius: '12px 12px 0 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                        {!photoErr && c.has_photo ? (
                            <img src={`/api/hr-candidates/candidates/${c.id}/photo`} alt="" style={{ ...avatarStyle, border: '3px solid rgba(255,255,255,0.5)' }}
                                onError={() => setPhotoErr(true)} />
                        ) : (
                            <div style={{ ...avatarPlaceholder, border: '3px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', color: '#fff' }}>{initials(c.name)}</div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>{c.name}</div>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', marginTop: 4 }}>
                                {detail.position?.title}
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                                {c.email ? <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.84rem' }}>{c.email}</span> : null}
                                {c.email && c.phone ? <span style={{ color: 'rgba(255,255,255,0.4)' }}>|</span> : null}
                                {c.phone ? <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.84rem' }}>{c.phone}</span> : null}
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                            {!state.read_only ? (
                                <Select value={stage} onChange={changeStage} width="160px">
                                    {STAGES.map((s) => <option key={s} value={s}>{s.replace('_', '. ')}</option>)}
                                </Select>
                            ) : <Pill tone="neutral">{stage.replace('_', '. ')}</Pill>}
                            {c.avg_rating !== null ? (
                                <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '4px 12px', color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>
                                    {c.avg_rating.toFixed(1)} / 5
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>

                {/* Podaci ispod headera */}
                <div style={{ padding: '16px 24px' }}>
                    {!editing ? (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 }}>
                                <div><div style={label}>{t.expected}</div><div style={{ fontSize: '0.9rem', color: ui.ink }}>{c.expected_salary_eur != null ? `${Number(c.expected_salary_eur).toLocaleString('hr')} EUR` : '—'}</div></div>
                                <div><div style={label}>{t.gross}</div><div style={{ fontSize: '0.9rem', color: ui.ink }}>{c.gross_salary_eur != null ? `${Number(c.gross_salary_eur).toLocaleString('hr')} EUR` : '—'}</div></div>
                                <div><div style={label}>{t.notice}</div><div style={{ fontSize: '0.9rem', color: ui.ink }}>{c.notice_period ?? '—'}</div></div>
                            </div>
                            {c.comment ? (
                                <div style={{ marginTop: 12, padding: 10, background: ui.bg, borderRadius: ui.radiusSm, fontSize: '0.85rem', color: ui.ink2, lineHeight: 1.5 }}>{c.comment}</div>
                            ) : null}
                            <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                                {c.cv_original_name ? (
                                    <Btn variant="ghost" size="sm" onClick={() => openFileInline(`/hr-candidates/candidates/${c.id}/cv`, token)}>{t.cvDownload}: {c.cv_original_name}</Btn>
                                ) : <span style={{ color: ui.muted, fontSize: '0.85rem' }}>{t.cvNone}</span>}
                                {!state.read_only ? (
                                    <Btn variant="ghost" size="sm" onClick={() => setEditing(true)}>{t.edit}</Btn>
                                ) : null}
                            </div>
                        </>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <label><span style={label}>{t.name}</span><input style={field} value={editName} onChange={(e) => setEditName(e.target.value)} /></label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <label><span style={label}>{t.email}</span><input style={field} value={editEmail} onChange={(e) => setEditEmail(e.target.value)} /></label>
                                <label><span style={label}>{t.phone}</span><input style={field} value={editPhone} onChange={(e) => setEditPhone(e.target.value)} /></label>
                                <label><span style={label}>{t.expected}</span><input style={field} type="number" value={editExpected} onChange={(e) => setEditExpected(e.target.value)} /></label>
                                <label><span style={label}>{t.gross}</span><input style={field} type="number" value={editGross} onChange={(e) => setEditGross(e.target.value)} /></label>
                            </div>
                            <label><span style={label}>{t.notice}</span><input style={field} value={editNotice} onChange={(e) => setEditNotice(e.target.value)} /></label>
                            <label><span style={label}>{t.comment}</span><textarea style={{ ...field, height: 'auto', padding: 10, minHeight: 60 }} value={editComment} onChange={(e) => setEditComment(e.target.value)} /></label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Btn variant="primary" disabled={busy || !editName} onClick={saveEdit}>{t.editSave}</Btn>
                                <Btn variant="ghost" onClick={() => setEditing(false)}>{t.cancel}</Btn>
                            </div>
                        </div>
                    )}
                </div>

                {detail.position?.disqualifiers ? (
                    <div style={{ margin: '0 24px 16px', padding: 10, background: '#fde7ea', borderRadius: ui.radiusSm, fontSize: '0.85rem', color: '#8a1b34' }}>
                        <strong>{t.disqualifiers_}:</strong> {detail.position.disqualifiers}
                    </div>
                ) : null}
                {detail.position?.key_questions?.length ? (
                    <div style={{ margin: '0 24px 16px', fontSize: '0.85rem' }}>
                        <div style={{ ...label, marginBottom: 6 }}>{t.keyQuestions}</div>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>{detail.position.key_questions.map((q, i) => <li key={i} style={{ color: ui.ink2 }}>{q}</li>)}</ul>
                    </div>
                ) : null}
            </Card>

            {aiEval ? <AiProposalCard ev={aiEval} comp={comp} locale={locale} /> : null}

            {/* Postojeće ocjene voditelja */}
            <Card pad={18}>
                <div style={{ fontWeight: 700, color: ui.ink, marginBottom: 10 }}>{t.evalTitle} ({humanEvals.length})</div>
                {humanEvals.length === 0 ? <p style={{ color: ui.muted, fontSize: '0.86rem', margin: 0 }}>{t.noEvals}</p> : humanEvals.map((e) => (
                    <div key={e.id} style={{ borderTop: `1px solid ${ui.line2}`, paddingTop: 10, marginTop: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                            <strong style={{ color: ui.ink }}>{e.evaluator_name ?? '—'}</strong>
                            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                {e.disqualifier_triggered ? <Pill tone="bad">ISKLJUČIVAČ</Pill> : null}
                                {e.overall_rating ? <span style={{ fontSize: '0.85rem', color: ui.ink2 }}>★ {e.overall_rating}/5</span> : null}
                                {e.recommendation ? <Pill tone={recTone[e.recommendation] ?? 'neutral'}>{e.recommendation}</Pill> : null}
                            </span>
                        </div>
                        {e.disqualifier_note ? <div style={{ fontSize: '0.85rem', marginTop: 4, color: '#8a1b34' }}>{e.disqualifier_note}</div> : null}
                        {e.second_round_focus ? <div style={{ fontSize: '0.85rem', marginTop: 4, color: ui.ink2 }}><em>{t.secondRound}:</em> {e.second_round_focus}</div> : null}
                        {(() => {
                            const flags = comp.filter((k) => { const n = e.evidence?.[k.key]; return n && typeof n === 'object' && n.red_flag; }).map((k) => k.label);
                            return flags.length ? <div style={{ fontSize: '0.82rem', marginTop: 4, color: '#dc2626' }}>⚑ {t.redFlag}: {flags.join(', ')}</div> : null;
                        })()}
                    </div>
                ))}
            </Card>

            {/* Nova ocjena */}
            {!state.read_only ? (
                <Card pad={18}>
                    <div style={{ fontWeight: 700, color: ui.ink, marginBottom: 4 }}>{t.evalTitle}</div>
                    {err ? <p style={{ color: '#dc2626', fontSize: '0.85rem' }}>{err}</p> : null}
                    {comp.map((k) => (
                        <div key={k.key} style={{ borderTop: `1px solid ${ui.line2}`, paddingTop: 12, marginTop: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                <div>
                                    <div style={{ fontWeight: 700, color: ui.ink, fontSize: '0.88rem' }}>{k.label}</div>
                                    <div style={{ color: ui.muted, fontSize: '0.78rem' }}>{k.desc}</div>
                                </div>
                                <div style={{ width: 90 }}>
                                    <Select value={String(scores[k.key] ?? '')} onChange={(v) => setScores((s) => ({ ...s, [k.key]: v ? Number(v) : '' }))} width="100%">
                                        <option value="">—</option>
                                        {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                                    </Select>
                                </div>
                            </div>
                            <textarea style={{ ...field, height: 'auto', padding: 10, minHeight: 44, marginTop: 8 }} placeholder={t.evidence} value={evidence[k.key]?.note ?? ''} onChange={(ev) => setNote(k.key, { note: ev.target.value })} />
                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 6 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: evidence[k.key]?.red_flag ? '#dc2626' : ui.muted, whiteSpace: 'nowrap', paddingTop: 9 }}>
                                    <input type="checkbox" checked={!!evidence[k.key]?.red_flag} onChange={(ev) => setNote(k.key, { red_flag: ev.target.checked })} /> {t.redFlag}
                                </label>
                                <input style={field} placeholder={t.followUp} value={evidence[k.key]?.follow_up ?? ''} onChange={(ev) => setNote(k.key, { follow_up: ev.target.value })} />
                            </div>
                        </div>
                    ))}

                    <div style={{ borderTop: `2px solid ${ui.line2}`, paddingTop: 12, marginTop: 14 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.88rem', color: disq ? '#dc2626' : ui.ink }}>
                            <input type="checkbox" checked={disq} onChange={(e) => setDisq(e.target.checked)} /> {t.disqTrig}
                        </label>
                        {disq ? (
                            <textarea style={{ ...field, height: 'auto', padding: 10, minHeight: 44, marginTop: 8 }} placeholder={t.disqNote} value={disqNote} onChange={(e) => setDisqNote(e.target.value)} />
                        ) : null}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
                        <label><span style={label}>{t.overall}</span>
                            <Select value={String(overall)} onChange={(v) => setOverall(v ? Number(v) : '')} width="100%">
                                <option value="">—</option>
                                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} — {state.rating_anchors[String(n)]?.split(' — ')[0] ?? ''}</option>)}
                            </Select>
                        </label>
                        <label><span style={label}>{t.recommendation}</span>
                            <Select value={disq ? 'NE' : rec} onChange={setRec} width="100%">
                                <option value="">—</option>
                                {state.recommendations.map((r) => <option key={r} value={r}>{r}</option>)}
                            </Select>
                        </label>
                    </div>
                    <label style={{ display: 'block', marginTop: 12 }}><span style={label}>{t.secondRound}</span>
                        <textarea style={{ ...field, height: 'auto', padding: 10, minHeight: 50 }} value={second} onChange={(e) => setSecond(e.target.value)} />
                    </label>
                    <div style={{ marginTop: 14 }}><Btn variant="primary" disabled={busy} onClick={submit}>{t.save}</Btn></div>
                </Card>
            ) : null}
        </div>
    );
}

export default HrCandidateSelectionModule;
